import { createRef, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  IconButton,
  Link,
  Slide,
  Snackbar,
  Stack,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { Close } from '@mui/icons-material';
import ReCAPTCHA from 'react-google-recaptcha';
import { logEvent } from 'firebase/analytics';

import { contactSchema } from '../../schemas';
import PageTitle from '../../components/PageTitle/PageTitle';
import { StyledButton } from '../../components/Utils/UIUtils';
import { CustomInput } from '../../components/Forms/CustomInput';
import { CustomTextArea } from '../../components/Forms/CustomTextArea';
import FaqSummary from '../../components/Faq/FaqSummary';
import PreFooter from '../../components/Footer/PreFooter';
import { submitContactForm } from '../../actions/userActions';
import { MyHelmet } from './MyHelmet';
import { analytics } from '../../config/firebase';

function SlideTransition(props) {
  return <Slide {...props} direction="up" />;
}

export default function Contact() {
  const { t } = useTranslation();
  const [formikActions, setFormikActions] = useState(null);

  const dispatch = useDispatch();

  const theme = useTheme();

  const snackbarDuration = 4000;
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const reCaptchaRef = createRef();

  const handleOpenSnackbar = (severity) => {
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const { error, success, loading } = useSelector((state) => state.contactForm);

  useEffect(() => {
    if (success) {
      handleOpenSnackbar('success');
      reCaptchaRef.current.reset();
      if (formikActions) {
        formikActions.resetForm();
      }
    }
  }, [success, reCaptchaRef, formikActions]);

  useEffect(() => {
    if (error) {
      handleOpenSnackbar('error');
    }
  }, [error]);

  useEffect(() => {
    let timer;

    if (success || error) {
      timer = setTimeout(() => {
        dispatch({ type: 'CLEAR_CONTACT_FORM_STATUS' });
      }, snackbarDuration);
    }

    return () => clearTimeout(timer);
  }, [success, error, dispatch]);

  const onSubmit = (values, actions) => {
    const formData = {
      fullName: values.fullName,
      email: values.email,
      phone: values.phone,
      message: values.message,
    };
    setFormikActions(actions);
    dispatch(submitContactForm(formData));

    actions.setSubmitting(false);
  };

  // Firebase analytics
  useEffect(() => {
    logEvent(analytics, 'page_view', {
      page_title: 'Contact',
      page_location: window.location.href,
      page_path: window.location.pathname,
    });
  }, []);

  return (
    <>
      <MyHelmet />
      <PageTitle>
        <Stack
          component={'header'}
          gap={2}
          justifyContent="center"
          textAlign={'center'}
        >
          <Typography
            variant="h1"
            color="primary.neutral800"
            sx={{
              fontSize: '32px',
              fontWeight: '700',
              textTransform: 'uppercase',
            }}
          >
            {t('CONTACT.TITLE')}
          </Typography>
          <Typography variant="body1" color="primary.neutral500">
            <Trans
              i18nKey="CONTACT.DESCRIPTION"
              components={{
                Link1: (
                  <Link
                    href="/faq"
                    sx={{
                      fontWeight: '600',
                      textDecoration: 'none',
                    }}
                  />
                ),
                Link2: (
                  <Link
                    href="https://help.slex.tv/"
                    sx={{
                      fontWeight: '600',
                      textDecoration: 'none',
                    }}
                  />
                ),
              }}
            />
          </Typography>
        </Stack>
      </PageTitle>
      <Container
        maxWidth={'lg'}
        sx={{
          pt: '40px',
          pb: '80px',
        }}
      >
        <Stack
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <Box
            sx={{
              border: '1px solid',
              borderColor: 'primary.neutral200',
              borderRadius: theme.shape.defaultBorderRadius,
              p: 4,
              maxWidth: '650px',
              width: '100%',
              margin: '0 auto',
            }}
          >
            <Formik
              initialValues={{
                fullName: '',
                email: '',
                phone: '',
                message: '',
                recaptcha: '',
              }}
              validationSchema={() => contactSchema(t)}
              validateOnBlur={false}
              validateOnChange={false}
              onSubmit={onSubmit}
            >
              {({ setFieldValue, setSubmitting, handleBlur, values }) => {
                const handleBlurAndExecuteReCAPTCHA = (e) => {
                  if (!values.recaptcha) {
                    reCaptchaRef.current.execute();
                    setSubmitting(true);
                  }
                  handleBlur(e);
                };
                return (
                  <Stack gap={2} justifyContent={'center'}>
                    <Form
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                      }}
                      onBlur={handleBlurAndExecuteReCAPTCHA}
                      id="contact-form"
                      name="contact-form"
                    >
                      <CustomInput
                        label={t('CONTACT.FORM.FULLNAME')}
                        name="fullName"
                        id="fullName"
                        type="text"
                        autoComplete="off"
                        placeholder={t('CONTACT.FORM.FULLNAME_PLACEHOLDER')}
                        manualMarginBottom={'16px'}
                      />

                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        gap={{ xs: 0, sm: 2 }}
                        sx={{ width: '100%' }}
                      >
                        <CustomInput
                          label={t('CONTACT.FORM.EMAIL')}
                          name="email"
                          id="email"
                          type="text"
                          autoComplete="email"
                          placeholder={t('CONTACT.FORM.EMAIL_PLACEHOLDER')}
                          manualMarginBottom={'16px'}
                        />

                        <CustomInput
                          label={t('CONTACT.FORM.PHONE')}
                          name="phone"
                          id="phone"
                          type="text"
                          autoComplete="phone"
                          placeholder={t('CONTACT.FORM.PHONE_PLACEHOLDER')}
                          manualMarginBottom={'16px'}
                        />
                      </Stack>

                      <CustomTextArea
                        label={t('CONTACT.FORM.MESSAGE')}
                        name="message"
                        id="message"
                        type="text"
                        autoComplete="message"
                        placeholder={t('CONTACT.FORM.MESSAGE_PLACEHOLDER')}
                        multiline
                        rows={2}
                        manualMarginBottom={'16px'}
                      />

                      <StyledButton
                        variant="contained"
                        type="submit"
                        name="submit-newsletter-email"
                        sx={{
                          borderRadius: theme.shape.defaultBorderRadius,
                          px: 3,
                          mb: 2,
                          whiteSpace: 'nowrap',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 2,
                          bgcolor: success ? 'primary.success' : 'primary.main',
                          '&:hover': {
                            bgcolor: success ? 'primary.success' : '',
                          },
                        }}
                      >
                        {success
                          ? t('CONTACT.FORM.BUTTON_SENT')
                          : t('CONTACT.FORM.BUTTON_SEND')}
                        {loading && (
                          <CircularProgress color="inherit" size={'16px'} />
                        )}
                      </StyledButton>
                      <ReCAPTCHA
                        sitekey={process.env.REACT_APP_SITE_KEY}
                        ref={reCaptchaRef}
                        onChange={(value) => {
                          setFieldValue('recaptcha', value);
                          setSubmitting(false);
                        }}
                        size="invisible"
                      />
                      <Typography
                        component="small"
                        color="primary.neutral800"
                        sx={{
                          fontSize: '12px',
                          fontWeight: '400',
                          textAlign: 'center',
                          color: theme.palette.primary.neutral500,
                        }}
                      >
                        <Trans
                          i18nKey="GENERAL.RECAPTCHA_MESSAGE"
                          components={{
                            Link1: (
                              <Link
                                href="https://policies.google.com/privacy"
                                target="_blank"
                                rel="noopener noreferrer"
                                color="primary.main"
                              />
                            ),
                            Link2: (
                              <Link
                                href="https://policies.google.com/terms"
                                target="_blank"
                                rel="noopener noreferrer"
                                color="primary.main"
                              />
                            ),
                          }}
                        />
                      </Typography>
                    </Form>
                  </Stack>
                );
              }}
            </Formik>

            <Snackbar
              open={openSnackbar}
              autoHideDuration={snackbarDuration}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              TransitionComponent={SlideTransition}
            >
              <Alert
                onClose={handleCloseSnackbar}
                severity={snackbarSeverity}
                variant="filled"
                icon={false}
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={handleCloseSnackbar}
                  >
                    <Close fontSize="inherit" />
                  </IconButton>
                }
                sx={{
                  width: { xs: '90%', md: '100%' },
                  minHeight: '64px',
                  fontSize: '14px',
                  fontWeight: '400',
                  bgcolor:
                    snackbarSeverity === 'success'
                      ? theme.palette.primary.success97
                      : theme.palette.primary.danger97,
                  color: 'primary.neutral800',
                  boxShadow:
                    '0 4px 6px -1px rgba(0,0,0,.1),0 2px 4px -2px rgba(0,0,0,.15)',
                  border: '1px solid',
                  borderColor:
                    snackbarSeverity === 'success'
                      ? alpha(theme.palette.primary.success, 0.2)
                      : alpha(theme.palette.primary.danger, 0.2),
                  borderLeft: '4px solid',
                  borderLeftColor:
                    snackbarSeverity === 'success'
                      ? 'primary.success'
                      : 'primary.danger',
                  '& .MuiAlert-message': {
                    display: 'flex',
                    alignItems: 'flex-start',
                    flexDirection: 'column',
                    gap: '8px',
                  },
                  '& .MuiAlert-action': {
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  },
                  borderRadius: theme.shape.secondaryBorderRadius,
                }}
              >
                <Typography
                  variant="body1"
                  color="inherit"
                  fontSize={'16px'}
                  fontWeight={'600'}
                >
                  {snackbarSeverity === 'success'
                    ? t('CONTACT.FORM_SUBMITTED.TITLE')
                    : t('CONTACT.FORM_ERROR.TITLE')}
                </Typography>
                <Typography variant="body1" color="inherit" fontSize={'14px'}>
                  {snackbarSeverity === 'success'
                    ? t('CONTACT.FORM_SUBMITTED.MESSAGE')
                    : t('CONTACT.FORM_ERROR.MESSAGE')}
                </Typography>
              </Alert>
            </Snackbar>
          </Box>
          <Stack gap={4} sx={{ width: '100%' }}>
            <Typography
              variant="h2"
              sx={{ fontSize: '40px', fontWeight: '700', textAlign: 'center' }}
            >
              {t('HOME_PAGE.SECTION9.TITLE')}
            </Typography>
            <Box sx={{ width: { xs: '100%', md: '70%' }, mx: 'auto' }}>
              <FaqSummary />
            </Box>
          </Stack>
        </Stack>
      </Container>
      <PreFooter />
    </>
  );
}
